import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, Chip, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { RootState } from '../../reducers';
import { useAppDispatch } from '../../store/store';
import { deleteModifier, fetchAllModifiers, setSelectedModifier, setShowDeleteModal, setShowFormModal } from '../../store/adminCPTModifiersSlice.slice';
import { CPTModifier } from '../../Models/Shared/CPTModifier.model';
import AdminCPTModifierForm from './AdminCPTModifierForm';

const AdminCPTModifierList = () => {
    const dispatch = useAppDispatch();
    const { modifiers, showFormModal, showDeleteModal, selectedModifier } = useSelector((state: RootState) => state.adminCPTModifiersSlice);

    useEffect(() => {
        dispatch(fetchAllModifiers());
        document.title = 'CPT Modifiers';
    }, [dispatch]);

    const handleAdd = () => {
        dispatch(setSelectedModifier(new CPTModifier()));
        dispatch(setShowFormModal(true));
    };

    const handleEdit = (modifier) => {
        dispatch(setSelectedModifier(modifier));
        dispatch(setShowFormModal(true));
    };

    const handleDeleteConfirm = (modifier) => {
        dispatch(setSelectedModifier(modifier));
        dispatch(setShowDeleteModal(true));
    };

    const handleDelete = () => {
        dispatch(deleteModifier(selectedModifier.Id));
    };

    return (
        <Grid container spacing={2} p={2}>
            <Grid size={12} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">CPT Modifiers</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
                    Add Modifier
                </Button>
            </Grid>

            <Grid size={12}>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Code</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Applicable POS</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {modifiers.map((modifier) => (
                                <TableRow key={modifier.Id}>
                                    <TableCell>
                                        <strong>{modifier.Code}</strong>
                                    </TableCell>
                                    <TableCell>{modifier.Description}</TableCell>
                                    <TableCell>
                                        {modifier.ApplicablePOSValues.map((pos) => (
                                            <Chip key={pos} label={`POS ${pos}`} size="small" sx={{ mr: 0.5 }} />
                                        ))}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={modifier.IsActive ? 'Active' : 'Inactive'}
                                            color={modifier.IsActive ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="primary" onClick={() => handleEdit(modifier)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDeleteConfirm(modifier)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            <AdminCPTModifierForm open={showFormModal} onClose={() => dispatch(setShowFormModal(false))} />

            {showDeleteModal && (
                <Grid size={12}>
                    <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                        <Typography>
                            Delete modifier <strong>{selectedModifier?.Code}</strong>? This cannot be undone.
                        </Typography>
                        <Button variant="contained" color="error" onClick={handleDelete} sx={{ mt: 1, mr: 1 }}>
                            Delete
                        </Button>
                        <Button variant="outlined" onClick={() => dispatch(setShowDeleteModal(false))} sx={{ mt: 1 }}>
                            Cancel
                        </Button>
                    </Paper>
                </Grid>
            )}
        </Grid>
    );
};

export default AdminCPTModifierList;
